import { useState } from 'react'
import { useCriteriosDoContrato } from '../../../api/queries/parametrosContratuais'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro, Esqueleto } from '../../../components/Estados'
import { Cartao } from '../../../components/ListaDeDados'
import { TEXTOS } from '../../../lib/glossario'
import { dataHora } from '../../../lib/datas'
import { competenciaPorExtenso } from '../../chamados/competencia'
import type { VersaoDeCriterio } from '../../../types/parametroContratual'
import { DialogoDeVersaoDeCriterio } from './DialogoDeVersaoDeCriterio'

/** O mês corrente no fuso do contrato, em `AAAA-MM`. */
function mesDeHoje(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }).slice(0, 7)
}

/**
 * Como o sistema resolve as contradições do contrato — leitura, e uma escrita que é sempre versão.
 *
 * **Leitura primeiro, porque a lista responde a uma pergunta que nenhuma outra tela responde:** por
 * que um indicador volta sem número. O critério que consolida os quatro números do IIE só passa a
 * valer em 09/2026, e todas as competências com chamado são anteriores — enquanto essa linha não
 * estava em tela nenhuma, o fiscal via o IIE sem número e não tinha como saber por quê.
 *
 * **A escrita é versão nova, nunca edição.** O banco recusa `UPDATE` e `DELETE` nesta tabela: o
 * critério que sustentou um desconto assinado é a prova de qual regra valia. Alterar é acrescentar.
 *
 * @param props o mês corrente, para separar o que vale do que ainda não vale
 * @returns o cartão dos critérios
 */
export function CriteriosDoTr({ mesAtual = mesDeHoje() }: { mesAtual?: string }) {
  const { data: versoes, isPending, isError, error } = useCriteriosDoContrato()
  const [registrando, setRegistrando] = useState(false)
  const t = TEXTOS.referencias.criterios

  return (
    <Cartao
      titulo={t.titulo}
      descricao={t.ajuda}
      acao={
        <Botao aparencia="primario" tamanho="pequeno" onClick={() => setRegistrando(true)}>
          {t.registrar}
        </Botao>
      }
    >
      <p className="mb-3 max-w-prose rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">{t.semEdicao}</p>

      {isPending && <Esqueleto linhas={5} />}
      {isError && <AvisoDeErro erro={error} titulo={t.naoCarregou} />}

      {versoes && (
        <ul className="divide-y divide-slate-100">
          {versoes.map((versao) => (
            <LinhaDoCriterio key={`${versao.chave}-${versao.versao}`} versao={versao} mesAtual={mesAtual} />
          ))}
        </ul>
      )}

      {registrando && (
        <DialogoDeVersaoDeCriterio versoes={versoes ?? []} aoFechar={() => setRegistrando(false)} />
      )}
    </Cartao>
  )
}

const CORES: Record<string, string> = {
  FORMALIZADO: 'bg-green-50 text-green-900 ring-green-200',
  PROVISORIO: 'bg-amber-50 text-amber-900 ring-amber-200',
  INDETERMINADO: 'bg-slate-200 text-slate-900 ring-slate-400',
}

/** Uma versão na linha do tempo de um critério. */
function LinhaDoCriterio({ versao, mesAtual }: { versao: VersaoDeCriterio; mesAtual: string }) {
  const t = TEXTOS.referencias.criterios
  // `vigenciaFim` chega AUSENTE quando a vigência está em aberto — o backend omite campo nulo.
  const futura = versao.vigenciaInicio > mesAtual
  const inicio = competenciaPorExtenso(versao.vigenciaInicio)

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <span className="min-w-0 flex-1">
          <span className="block font-mono text-sm text-slate-900">{versao.chave}</span>
          <span className="mt-0.5 block text-xs text-slate-500">
            {t.valeDesde(inicio)}
            {versao.vigenciaFim ? t.valeAte(competenciaPorExtenso(versao.vigenciaFim)) : ''}
          </span>
        </span>
        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${CORES[versao.status]}`}
          title={t.seloAjuda[versao.status]}
        >
          {t.selo[versao.status]}
        </span>
      </div>

      {futura && (
        <p className="mt-2 max-w-prose rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {t.aindaNaoVale(inicio)}
        </p>
      )}
      {versao.status === 'INDETERMINADO' && (
        <p className="mt-2 max-w-prose text-sm text-slate-700">{t.seloAjuda.INDETERMINADO}</p>
      )}

      <p className="mt-2 max-w-prose text-sm text-slate-700">{versao.fundamento}</p>
      <p className="mt-1 max-w-prose text-xs text-slate-500">{versao.fonteTr}</p>
      {versao.riscoResidual && (
        <p className="mt-1 max-w-prose text-xs text-slate-500">
          <span className="font-medium">{t.risco}</span> {versao.riscoResidual}
        </p>
      )}
      {versao.atoFormal && (
        <p className="mt-1 max-w-prose text-xs text-slate-500">
          <span className="font-medium">{t.ato}</span> {versao.atoFormal}
        </p>
      )}
      <pre className="mt-2 whitespace-pre-wrap break-words rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700">
        {versao.valor}
      </pre>
      <p className="mt-1 text-xs text-slate-400">
        {versao.registradoPorNome
          ? t.registradoPor(versao.registradoPorNome, dataHora(versao.registradoEm))
          : t.semRegistro}
      </p>
    </li>
  )
}
